import React, { useEffect, useState } from "react";
import moment from "moment";
import { useSnackbar } from "notistack";
import Button1 from "../../../components/form/Button1";
import TextInput from "../../../components/form/TextInput";
import Checkbox from "../../../components/form/Checkbox";
import Modal1 from "../../../components/DIsplay/Modal/Modal1";
import CopyButton from "../../../components/mini/CopyButton";
import getPdfCredentials from "../../../controllers/settings/pdf/getPdfCredentials";
import createPdfCredential from "../../../controllers/settings/pdf/createPdfCredential";
import updatePdfCredential from "../../../controllers/settings/pdf/updatePdfCredential";
import deletePdfCredential from "../../../controllers/settings/pdf/deletePdfCredential";

function credentialId(row) {
  return row?.id || row?._id || "";
}

const emptyForm = {
  label: "",
  clientId: "",
  clientSecret: "",
  enabled: true,
};

/**
 * Agency PDF — Adobe PDF credential CRUD via /api/v1/credentials on the app API base.
 */
export default function PDFSettings() {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  async function load() {
    setLoading(true);
    const res = await getPdfCredentials();
    setLoading(false);
    if (res.return) {
      setRows(Array.isArray(res.data) ? res.data : []);
    } else {
      enqueueSnackbar(res.msg || "Failed to load PDF credentials", {
        variant: "error",
      });
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate() {
    setForm(emptyForm);
    setCreateOpen(true);
  }

  function openEdit(row) {
    setForm({
      label: row?.label || "",
      clientId: row?.clientId || "",
      clientSecret: "",
      enabled: row?.enabled !== false,
    });
    setEditTarget(row);
  }

  async function handleCreate(ev) {
    ev.preventDefault();
    if (!form.label.trim() || !form.clientId.trim() || !form.clientSecret.trim()) {
      enqueueSnackbar("Label, client ID, and client secret are required", {
        variant: "warning",
      });
      return;
    }
    setSaving(true);
    const res = await createPdfCredential({
      label: form.label.trim(),
      clientId: form.clientId.trim(),
      clientSecret: form.clientSecret.trim(),
      enabled: Boolean(form.enabled),
    });
    setSaving(false);
    if (res.return) {
      enqueueSnackbar(res.msg || "PDF credential created", { variant: "success" });
      setCreateOpen(false);
      setForm(emptyForm);
      load();
    } else {
      enqueueSnackbar(res.msg || "Failed to create credential", {
        variant: "error",
      });
    }
  }

  async function handleUpdate(ev) {
    ev.preventDefault();
    const id = credentialId(editTarget);
    if (!id) return;
    if (!form.label.trim() || !form.clientId.trim()) {
      enqueueSnackbar("Label and client ID are required", { variant: "warning" });
      return;
    }
    const payload = {
      label: form.label.trim(),
      clientId: form.clientId.trim(),
      enabled: Boolean(form.enabled),
    };
    if (form.clientSecret.trim()) {
      payload.clientSecret = form.clientSecret.trim();
    }
    setSaving(true);
    const res = await updatePdfCredential(id, payload);
    setSaving(false);
    if (res.return) {
      enqueueSnackbar(res.msg || "PDF credential updated", { variant: "success" });
      setEditTarget(null);
      setForm(emptyForm);
      load();
    } else {
      enqueueSnackbar(res.msg || "Failed to update credential", {
        variant: "error",
      });
    }
  }

  async function handleToggleEnabled(row) {
    const id = credentialId(row);
    if (!id) return;
    setSaving(true);
    const res = await updatePdfCredential(id, { enabled: !row.enabled });
    setSaving(false);
    if (res.return) {
      enqueueSnackbar(res.msg || "Updated", { variant: "success" });
      load();
    } else {
      enqueueSnackbar(res.msg || "Failed to update", { variant: "error" });
    }
  }

  async function handleDelete() {
    const id = credentialId(deleteTarget);
    if (!id) return;
    setSaving(true);
    const res = await deletePdfCredential(id);
    setSaving(false);
    if (res.return) {
      enqueueSnackbar(res.msg || "PDF credential deleted", { variant: "success" });
      setDeleteTarget(null);
      load();
    } else {
      enqueueSnackbar(res.msg || "Failed to delete credential", {
        variant: "error",
      });
    }
  }

  return (
    <div className="flex flex-col gap-4 flex-1">
      <div className="flex gap-4 justify-between items-start flex-wrap">
        <div>
          <h5 className="text-[#0F172A]">PDF credentials</h5>
          <p className="text-[13px] text-[#6D7C94] mt-1">
            Adobe PDF service credentials used for document generation. Secrets
            are never shown in full after create — only the last 4 characters.
          </p>
        </div>
        <Button1 className="!w-auto" onClick={openCreate}>
          Add credential
        </Button1>
      </div>

      <div className="bg-white rounded-xl border border-[#E3E8F0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#EEF1F6]">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
                  Label
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
                  Client ID
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
                  Secret
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
                  Created
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EEF1F6]">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j} className="px-4 py-4">
                        <div className="h-4 bg-[#E3E8F0] rounded w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length ? (
                rows.map((row) => {
                  const id = credentialId(row);
                  return (
                    <tr key={id || row.clientId} className="hover:bg-[#F8FAFC]/50">
                      <td className="px-4 py-3 text-[13px] font-semibold text-[#0F172A]">
                        {row.label || "—"}
                        {row.lastError ? (
                          <p
                            className="text-[11px] font-normal text-[#B42318] mt-0.5 max-w-[220px] truncate"
                            title={row.lastError}
                          >
                            {row.lastError}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-[13px] text-[#0F172A]">
                        <div className="flex items-center gap-2">
                          <span
                            id={`pdf-clientId-${id}`}
                            className="font-mono text-xs max-w-[160px] truncate block"
                            title={row.clientId}
                          >
                            {row.clientId || "—"}
                          </span>
                          {row.clientId ? (
                            <CopyButton id={`pdf-clientId-${id}`} />
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[13px] font-mono text-[#6D7C94]">
                        {row.secretLast4 ? `••••${row.secretLast4}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-[13px]">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleToggleEnabled(row)}
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                            row.enabled
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {row.enabled ? "Enabled" : "Disabled"}
                        </button>
                        {row.cooldownUntil ? (
                          <p className="text-[11px] text-[#6D7C94] mt-1">
                            Cooldown until{" "}
                            {moment(row.cooldownUntil).format("DD MMM, HH:mm")}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-[13px] text-[#0F172A] whitespace-nowrap">
                        {row.createdAt
                          ? moment(row.createdAt).format("DD MMM YYYY, HH:mm")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-[13px]">
                        <div className="flex items-center gap-3 whitespace-nowrap">
                          <button
                            type="button"
                            className="font-semibold text-primary1 hover:underline"
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="font-semibold text-[#B42318] hover:underline"
                            onClick={() => setDeleteTarget(row)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-[13px] text-[#6D7C94]"
                  >
                    No PDF credentials yet. Add an Adobe account to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal1 open={createOpen} setOpen={setCreateOpen}>
        <form
          onSubmit={handleCreate}
          className="p-5 flex flex-col gap-4 max-w-[520px] w-[90vw]"
        >
          <div>
            <h5>Add PDF credential</h5>
            <p className="text-[12px] text-[#6D7C94] mt-1">
              Store an Adobe PDF Services client ID and secret.
            </p>
          </div>
          <TextInput
            label="Label"
            placeholder="e.g. Adobe account 1"
            value={form.label}
            onChange={(ev) => setForm({ ...form, label: ev.target.value })}
          />
          <TextInput
            label="Client ID"
            value={form.clientId}
            onChange={(ev) => setForm({ ...form, clientId: ev.target.value })}
          />
          <TextInput
            label="Client secret"
            type="password"
            value={form.clientSecret}
            onChange={(ev) =>
              setForm({ ...form, clientSecret: ev.target.value })
            }
          />
          <Checkbox
            checked={form.enabled}
            onChange={(ev) =>
              setForm({ ...form, enabled: ev.target.checked })
            }
          >
            Enabled
          </Checkbox>
          <div className="flex gap-2 justify-end pt-1">
            <Button1
              variant="text"
              className="!w-auto"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button1>
            <Button1 type="submit" className="!w-auto" loading={saving}>
              Create
            </Button1>
          </div>
        </form>
      </Modal1>

      <Modal1 open={Boolean(editTarget)} setOpen={() => setEditTarget(null)}>
        <form
          onSubmit={handleUpdate}
          className="p-5 flex flex-col gap-4 max-w-[520px] w-[90vw]"
        >
          <div>
            <h5>Edit PDF credential</h5>
            <p className="text-[12px] text-[#6D7C94] mt-1">
              Leave client secret blank to keep the current secret. Saving a new
              secret or enabling clears cooldown and last error.
            </p>
          </div>
          <TextInput
            label="Label"
            value={form.label}
            onChange={(ev) => setForm({ ...form, label: ev.target.value })}
          />
          <TextInput
            label="Client ID"
            value={form.clientId}
            onChange={(ev) => setForm({ ...form, clientId: ev.target.value })}
          />
          <TextInput
            label="Client secret (optional)"
            type="password"
            placeholder={
              editTarget?.secretLast4
                ? `••••${editTarget.secretLast4}`
                : "Leave blank to keep"
            }
            value={form.clientSecret}
            onChange={(ev) =>
              setForm({ ...form, clientSecret: ev.target.value })
            }
          />
          <Checkbox
            checked={form.enabled}
            onChange={(ev) =>
              setForm({ ...form, enabled: ev.target.checked })
            }
          >
            Enabled
          </Checkbox>
          <div className="flex gap-2 justify-end pt-1">
            <Button1
              variant="text"
              className="!w-auto"
              onClick={() => setEditTarget(null)}
            >
              Cancel
            </Button1>
            <Button1 type="submit" className="!w-auto" loading={saving}>
              Save
            </Button1>
          </div>
        </form>
      </Modal1>

      <Modal1
        open={Boolean(deleteTarget)}
        setOpen={() => setDeleteTarget(null)}
      >
        <div className="p-5 flex flex-col gap-4 max-w-[440px] w-[90vw]">
          <h5>Delete PDF credential</h5>
          <p className="text-[13px] text-[#6D7C94]">
            Remove{" "}
            <span className="font-semibold text-[#0F172A]">
              {deleteTarget?.label || "this credential"}
            </span>
            ? This cannot be undone.
          </p>
          <div className="flex gap-2 justify-end">
            <Button1
              variant="text"
              className="!w-auto"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button1>
            <Button1
              className="!w-auto !bg-red-500 !text-white"
              loading={saving}
              onClick={handleDelete}
            >
              Delete
            </Button1>
          </div>
        </div>
      </Modal1>
    </div>
  );
}
